/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { apiUrl } from '../common/constants';
import LocalStorageSvc, {
  JWT_KEY
} from '../common/localStorageSvc';
import useAuthStore from '../datastore/useAuth';
import requestManager from '../requestManager';

interface NormalizedAxiosError {
  status: number;
  statusText: string;
  data: {
    message?: string;
    [key: string]: any;
  };
}

const getAxiosError = (error: any): NormalizedAxiosError | null => {
  if (
    error?.response?.status &&
    error?.response?.statusText
  ) {
    return {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response?.data || null,
    };
  }
  if (
    error &&
    error.status &&
    error.statusText
  ) {
    return {
      status: error.status,
      statusText: error.statusText,
      data: error.data || null,
    };
  }
  return null
}
const getAccountSuspendedError = (err: NormalizedAxiosError | null): string | null => {
  if (err?.status === 403 && err?.data?.message === 'Account Suspended') {
    useAuthStore.getState().setSuspended()
    return 'Forbidden: Account Suspended'
  }
  return null
}

const getAxiosErrorMessage = (err: NormalizedAxiosError | null): string | null => {
  if (!err) {
    return null
  }
  if (err?.data?.message) {
    return `${err.statusText}: ${err.data.message}`
  }
  return err?.statusText || null
}

export const handleError = (error: any): { message: string, suspended: boolean } => {
  const axError = getAxiosError(error)
  if (axError) {
    let message = getAccountSuspendedError(axError)
    if (message) {
      return { message, suspended: true }
    }
    message = getAxiosErrorMessage(axError)
    if (message) {
      return { message, suspended: false }
    }
  }
  return { message: 'Something went wrong', suspended: false }
}




const isExpired = (token: string): boolean => {
  const decoded = JSON.parse(atob(token.split('.')[1]));
  return decoded.exp < Date.now() / 1000;
};



const checkJwt = async (): Promise<string> => {
  let jwt = LocalStorageSvc.load<string>(JWT_KEY);
  if (jwt && !isExpired(jwt)) {
    return jwt
  }
  const response = await useAuthStore.getState().refreshToken()
  if (!response) {
    return ''
  }
  jwt = LocalStorageSvc.load<string>(JWT_KEY);
  return jwt || ''
}

axios.interceptors.request.use(
  async function (
    request: InternalAxiosRequestConfig<any>,
  ): Promise<InternalAxiosRequestConfig<any>> {
    const jwt = await checkJwt()
    if (jwt) {
      request.headers.set('Authorization', `Bearer ${jwt}`);
    }
    request.headers.set('Content-Type', 'application/json');
    return request;
  },
);


// New generic request method
async function makeRequest<T = AxiosResponse<any, any>>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  endpoint: string,
  data?: any
): Promise<T> {
  const url = `${apiUrl}${endpoint}`;

  const key = requestManager.getKey(
    url,
    data ? JSON.stringify(data) : undefined
  );

  const response = await requestManager.request<T>(`${method}:${key}`, () => {
    switch (method) {
      case 'get':
        return axios.get(url);
      case 'post':
        return axios.post(url, data);
      case 'put':
        return axios.put(url, data);
      case 'delete':
        return axios.delete(url);
      case 'patch':
        return axios.patch(url, data);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  });

  return response;
}

export const getRequest = (endpoint: string) =>
  makeRequest('get', endpoint);

export const postRequest = (endpoint: string, data: any) =>
  makeRequest('post', endpoint, data);

export const putRequest = (endpoint: string, data: any) =>
  makeRequest('put', endpoint, data);

export const deleteRequest = (endpoint: string) =>
  makeRequest('delete', endpoint);

export const patchRequest = (endpoint: string, data: any) =>
  makeRequest('patch', endpoint, data);